import React from "react";
import { cn } from "../../lib";
import { VariantProps, cva } from "class-variance-authority";
import { useBreakpoint } from "../../hooks/use-breakpoint";

// Define variant-specific style defaults
interface IVariantDefault {
    weight?:
        | "light"
        | "normal"
        | "medium"
        | "semibold"
        | "bold"
        | "extrabold"
        | "black";
    lineHeight?: "none" | "tight" | "snug" | "normal" | "relaxed" | "loose";
    tracking?: "tighter" | "tight" | "normal" | "wide" | "wider" | "widest";
}

const variantDefaults: Record<string, IVariantDefault> = {
    h1: { weight: "extrabold", lineHeight: "tight", tracking: "tight" },
    h2: { weight: "bold", lineHeight: "relaxed", tracking: "tight" },
    h3: { weight: "semibold", tracking: "tight" },
    h4: { weight: "semibold", tracking: "tight" },
    h5: { weight: "semibold", tracking: "tight" },
    h6: { weight: "semibold", tracking: "tight" },
    p: { weight: "normal", lineHeight: "normal" },
    span: { weight: "normal" },
    lead: { weight: "normal" },
    large: { weight: "semibold" },
    small: { weight: "medium", lineHeight: "none" },
    muted: { weight: "normal" },
    blockquote: { weight: "normal" },
};

const typographyVariants = cva("", {
    variants: {
        variant: {
            h1: "scroll-m-20 lg:text-6xl text-4xl font-extrabold !leading-[1.2] tracking-tight",
            h2: "scroll-m-20 lg:text-5xl text-3xl font-bold !leading-[1.2] tracking-tight first:mt-0",
            h3: "scroll-m-20 lg:text-4xl text-3xl font-semibold tracking-tight",
            h4: "scroll-m-20 lg:text-3xl text-2xl font-semibold tracking-tight",
            h5: "scroll-m-20 lg:text-2xl text-xl font-medium tracking-tight",
            h6: "scroll-m-20 lg:text-xl text-base font-medium tracking-tight",
            p: "leading-7 lg:text-lg text-base",
            span: "text-base",
            lead: "text-xl text-muted-foreground",
            large: "text-lg font-semibold",
            small: "text-sm font-medium leading-none",
            xs: "text-xs fo leading-none",
            muted: "text-sm text-muted-foreground",
            blockquote: "mt-6 border-l-2 pl-6 italic",
        },
        weight: {
            light: "font-light",
            normal: "font-normal",
            medium: "font-medium",
            semibold: "font-semibold",
            bold: "font-bold",
            extrabold: "font-extrabold",
            black: "font-black",
        },
        size: {
            xs: "text-xs",
            sm: "text-sm",
            base: "text-base",
            lg: "text-lg",
            xl: "text-xl",
            "2xl": "text-2xl",
            "3xl": "text-3xl",
            "4xl": "text-4xl",
            "5xl": "text-5xl",
            "6xl": "text-6xl",
        },
        // Responsive size variants that will be used on mobile
        mobileSize: {
            xs: "text-xs",
            sm: "text-sm",
            base: "text-base",
            lg: "text-lg",
            xl: "text-xl",
            "2xl": "text-2xl",
            "3xl": "text-3xl",
            "4xl": "text-4xl",
        },
        align: {
            left: "text-left",
            center: "text-center",
            right: "text-right",
            justify: "text-justify",
        },
        tracking: {
            tighter: "tracking-tighter",
            tight: "tracking-tight",
            normal: "tracking-normal",
            wide: "tracking-wide",
            wider: "tracking-wider",
            widest: "tracking-widest",
        },
        lineHeight: {
            none: "leading-none",
            tight: "leading-tight",
            snug: "leading-snug",
            normal: "leading-[1.1]",
            relaxed: "leading-relaxed",
            loose: "leading-loose",
        },
        transform: {
            uppercase: "uppercase",
            lowercase: "lowercase",
            capitalize: "capitalize",
            normalCase: "normal-case",
        },
        responsive: {
            true: "",
            false: "",
        },
    },
    defaultVariants: {
        variant: "p",
        weight: "normal",
        align: "left",
        tracking: "normal",
        lineHeight: "normal",
        responsive: true,
    },
});

export interface ITypographyProps
    extends React.HTMLAttributes<HTMLElement>,
        VariantProps<typeof typographyVariants> {
    as?: React.ElementType;
    color?: string;
    mobileSize?: "xs" | "sm" | "base" | "lg" | "xl" | "2xl" | "3xl" | "4xl";
}

export function Typography({
    className,
    variant,
    weight,
    size,
    mobileSize,
    align,
    tracking,
    lineHeight,
    transform,
    as,
    color,
    responsive = true,
    ...props
}: ITypographyProps) {
    // Use the mobile hook to determine screen size
    const { isMobile } = useBreakpoint();

    // Determine the element to render based on variant or explicit override
    const Component = as || getDefaultElement(variant as string);

    // Apply custom color if provided, using Tailwind's arbitrary value syntax
    const colorClass = color ?? "";

    // Get the defaults for the selected variant
    const currentVariantDefaults = variant
        ? variantDefaults[variant as keyof typeof variantDefaults] || {}
        : {};

    // Apply variant defaults but allow explicit props to override them
    const effectiveWeight = weight || currentVariantDefaults.weight;
    const effectiveTracking = tracking || currentVariantDefaults.tracking;
    const effectiveLineHeight = lineHeight || currentVariantDefaults.lineHeight;

    // Auto-adjust size for mobile if responsive is true and mobileSize is not explicitly set
    let effectiveSize = size;
    if (responsive && isMobile) {
        if (mobileSize) {
            // Use explicitly provided mobile size if available
            effectiveSize = mobileSize;
        } else if (size) {
            // Auto-scale down sizes larger than xl on mobile
            const sizeMap = {
                "6xl": "4xl",
                "5xl": "3xl",
                "4xl": "2xl",
                "3xl": "xl",
                "2xl": "xl",
                xl: "base",
                lg: "base",
                base: "base",
                sm: "sm",
                xs: "sm",
            };
            effectiveSize = (sizeMap[size] ?? size) as
                | "xs"
                | "sm"
                | "base"
                | "lg"
                | "xl"
                | "2xl"
                | "3xl"
                | "4xl"
                | "5xl"
                | "6xl"
                | null
                | undefined;
        }
    }

    return (
        <Component
            className={cn(
                typographyVariants({
                    variant,
                    weight: effectiveWeight,
                    size: effectiveSize,
                    align,
                    tracking: effectiveTracking,
                    lineHeight: effectiveLineHeight,
                    transform,
                }),
                colorClass,
                className,
            )}
            {...props}
        />
    );
}

// Helper to map variants to HTML elements if no override is provided
function getDefaultElement(variant: string | undefined): React.ElementType {
    switch (variant) {
        case "h1":
            return "h1";
        case "h2":
            return "h2";
        case "h3":
            return "h3";
        case "h4":
            return "h4";
        case "h5":
            return "h5";
        case "h6":
            return "h6";
        case "blockquote":
            return "blockquote";
        case "lead":
        case "large":
        case "small":
        case "muted":
        default:
            return "p";
    }
}
